import { env } from '@/lib/env';
import { getAuthItem, AUTH_STORAGE_KEYS } from '@/lib/authStorage';
import { getActiveWorkspaceId } from './workspace-context';

function toHeaders(initHeaders?: HeadersInit): Headers {
  if (initHeaders instanceof Headers) return new Headers(initHeaders);
  return new Headers(initHeaders ?? {});
}

function getUserId() {
  if (typeof window !== 'undefined') {
    // First try authenticated user ID from auth storage
    const userId = getAuthItem('userId');
    if (userId) return userId;

    // Fallback to guest ID from localStorage (set by SessionProvider)
    const guestId = localStorage.getItem('xavior_guest_id');
    if (guestId) return guestId;
  }
  return env.defaultUserId ?? 'anonymous';
}

export class ApiError extends Error {
  readonly status: number;
  readonly payload?: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      payload = await response.json();
    } else {
      payload = await response.text();
    }
  } catch {
    payload = undefined;
  }

  // API error bodies use { error: string }; some legacy/proxy paths use { message }.
  // Surface whichever is present before falling back to a generic status message.
  const errBody = typeof payload === 'object' && payload ? (payload as any) : undefined;
  const message =
    (typeof errBody?.message === 'string' && errBody.message) ||
    (typeof errBody?.error === 'string' && errBody.error) ||
    `Request failed with status ${response.status}`;

  return new ApiError(message, response.status, payload);
}

function makeUrl(path: string): string {
  if (path.startsWith('http')) return path;
  // Normalize: ensure base has no trailing slash, path has leading slash
  const base = env.apiBaseUrl.endsWith('/') ? env.apiBaseUrl.slice(0, -1) : env.apiBaseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function makeIdempotencyKey() {
  return crypto.randomUUID();
}

export interface ApiResponse<T> {
  data: T;
  etag?: string;
}

export async function apiGet<T>(url: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());

  // Add Authorization header if authToken is provided in headers
  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetch(makeUrl(url), {
    ...init,
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const etag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag };
}

export async function apiPost<T>(url: string, body: unknown, init?: RequestInit): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('Content-Type', 'application/json');
  // Respect caller-provided keys so callers can scope dedup per resource
  // (e.g. "create-contact-${email}") and double-click protection actually
  // works server-side. Default to a fresh UUID otherwise.
  if (!headers.has('Idempotency-Key')) headers.set('Idempotency-Key', makeIdempotencyKey());
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());

  // Add Authorization header if authToken is provided in headers
  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetch(makeUrl(url), {
    method: 'POST',
    ...init,
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const etag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag };
}

export async function apiPatch<T>(
  url: string,
  body: unknown,
  etag: string,
  init?: RequestInit,
): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Idempotency-Key', makeIdempotencyKey());
  headers.set('If-Match', etag);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());

  // Add Authorization header if authToken is provided in headers
  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetch(makeUrl(url), {
    method: 'PATCH',
    ...init,
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const newEtag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag: newEtag };
}

export async function apiDelete(url: string, init?: RequestInit): Promise<void> {
  const headers = toHeaders(init?.headers);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());

  // Add Authorization header if authToken is provided in headers
  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetch(makeUrl(url), {
    method: 'DELETE',
    ...init,
    headers,
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);
}
